/**
 * SqlField — pole formularza z edytorem SQL (zamiast `<Textarea className="font-mono">`).
 *
 * Opakowuje leniwy `LazySqlEditor` w ramkę pola (stan błędu jak `Textarea`) i dokłada przycisk
 * „Pełny ekran" — ten sam edytor w dużym oknie, na tej samej wartości. Kładź go jako JEDYNE
 * dziecko `FormField`: przyjmuje wstrzyknięte `id` / `aria-invalid` / `aria-describedby`.
 *
 * Ref (`SqlEditorHandle`) sięga edytora w polu (np. `insertAtCursor` dla przycisków parametrów).
 *
 * Błąd z „Testuj" (`diagnostics`, zwykle z `useSqlTestDiagnostics`): podkreślenie w treści,
 * czerwona ramka i „Błąd w linii N" pod polem.
 *
 * Podpowiedzi: `schema` (tabele → kolumny tenanta, zwykle z `useTenantSqlSchema`) i `parameters`
 * (`:nazwa` z opisem — zarezerwowane parametry miejsca + zadeklarowane w formularzu).
 *
 * Długie linie ZAWIJAJĄ się do szerokości pola (bez poziomego przewijania), a „Formatuj"
 * (w polu i w oknie pełnego ekranu) układa zapytanie w czytelne linie — ten sam formater co
 * w przeglądarce bazy tenanta (panel ITE), z parametrami `:nazwa` zostawionymi bez zmian.
 */

import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlignLeft, Maximize2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { IconButton } from '@/components/IconButton';
import { Modal } from '@/components/Modal';
import { useOptionalToast } from '@/contexts/ToastContext';
import { cn } from '@/lib/utils';

import {
  LazySqlEditor,
  type SqlEditorCompletion,
  type SqlEditorDiagnostic,
  type SqlEditorHandle,
} from './LazySqlEditor';

export interface SqlFieldProps {
  value: string;
  onChange: (value: string) => void;
  schema?: Record<string, string[]>;
  parameters?: SqlEditorCompletion[];
  diagnostics?: SqlEditorDiagnostic[];
  readOnly?: boolean;
  placeholder?: string;
  minHeight?: string;
  /** Tytuł okna pełnego ekranu (zwykle etykieta pola). */
  fullscreenTitle?: string;
  'aria-label': string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'data-testid'?: string;
  id?: string;
  className?: string;
}

export const SqlField = forwardRef<SqlEditorHandle, SqlFieldProps>(function SqlField(
  {
    value,
    onChange,
    schema,
    parameters,
    diagnostics,
    readOnly,
    placeholder,
    minHeight = '150px',
    fullscreenTitle,
    className,
    id,
    'aria-label': ariaLabel,
    'aria-describedby': describedBy,
    'aria-invalid': invalid,
    'data-testid': testId,
  },
  ref,
) {
  const { t } = useTranslation();
  const toast = useOptionalToast();
  const [fullscreen, setFullscreen] = useState(false);
  const canFormat = !readOnly && value.trim().length > 0;

  // Formater ładowany leniwie (jak w przeglądarce bazy) — nie trafia do głównego bundla.
  // Doczytanie też jest w `try`: nieudane (brak sieci, nowe wdrożenie) daje ten sam komunikat
  // zamiast cichego braku reakcji przycisku.
  const format = async () => {
    try {
      const { format: fmt } = await import('sql-formatter');
      onChange(
        fmt(value, {
          language: 'postgresql',
          keywordCase: 'upper',
          tabWidth: 2,
          paramTypes: { named: [':'] },
        }),
      );
    } catch {
      toast?.show({ variant: 'warning', title: t('common.sqlField.formatFailed') });
    }
  };
  // Linia pierwszego błędu (np. z „Testuj") — podkreślenie jest w edytorze, numer linii pod polem.
  const firstError = diagnostics?.[0];
  const errorLine =
    firstError === undefined ? null : value.slice(0, firstError.from).split('\n').length;

  return (
    <div className={className}>
      <div
        className={cn(
          'relative overflow-hidden rounded-lg border bg-bg-primary',
          invalid || errorLine !== null
            ? 'border-[rgb(var(--color-error))]'
            : 'border-border-secondary',
          readOnly && 'bg-bg-tertiary',
        )}
      >
        <LazySqlEditor
          ref={ref}
          value={value}
          onChange={onChange}
          schema={schema}
          parameters={parameters}
          diagnostics={diagnostics}
          readOnly={readOnly}
          placeholder={placeholder}
          minHeight={minHeight}
          id={id}
          aria-label={ariaLabel}
          indentWithTab={false}
          lineWrapping
          aria-describedby={describedBy}
          aria-invalid={invalid}
          data-testid={testId}
        />
        <div className="absolute right-1 top-1 z-10 flex gap-0.5 rounded bg-bg-primary">
          {canFormat && (
            <IconButton
              size="sm"
              variant="ghost"
              onClick={() => void format()}
              aria-label={t('common.sqlField.format')}
              title={t('common.sqlField.format')}
              data-testid={testId ? `${testId}-format` : undefined}
            >
              <AlignLeft aria-hidden="true" />
            </IconButton>
          )}
          <IconButton
            size="sm"
            variant="ghost"
            onClick={() => setFullscreen(true)}
            aria-label={t('common.sqlField.fullscreen')}
            title={t('common.sqlField.fullscreen')}
            data-testid={testId ? `${testId}-fullscreen-open` : undefined}
          >
            <Maximize2 aria-hidden="true" />
          </IconButton>
        </div>

        <Modal
          open={fullscreen}
          onClose={() => setFullscreen(false)}
          title={fullscreenTitle ?? ariaLabel}
          size="full"
          fixedHeight
          footer={
            <>
              {canFormat && (
                <Button
                  variant="secondary"
                  onClick={() => void format()}
                  leftIcon={<AlignLeft aria-hidden="true" />}
                  data-testid={testId ? `${testId}-fullscreen-format` : undefined}
                >
                  {t('common.sqlField.format')}
                </Button>
              )}
              <Button variant="secondary" onClick={() => setFullscreen(false)}>
                {t('common.close')}
              </Button>
            </>
          }
        >
          {fullscreen && (
            <div className="h-full overflow-hidden rounded-lg border border-border-secondary">
              <LazySqlEditor
                value={value}
                onChange={onChange}
                schema={schema}
                parameters={parameters}
                diagnostics={diagnostics}
                readOnly={readOnly}
                placeholder={placeholder}
                height="calc(85vh - 160px)"
                aria-label={ariaLabel}
                indentWithTab={false}
                lineWrapping
                data-testid={testId ? `${testId}-fullscreen` : undefined}
              />
            </div>
          )}
        </Modal>
      </div>
      {errorLine !== null && (
        <p
          className="mt-1 text-xs text-[rgb(var(--color-error))]"
          data-testid={testId ? `${testId}-error-line` : undefined}
        >
          {t('common.sqlField.errorAtLine', { line: errorLine })}
        </p>
      )}
    </div>
  );
});
